import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bookmark, Eye, Flag, Pencil, Share2, Star, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Progress } from "./ui/progress";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import api, { withBackendUrl } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const MediaImage = ({ src, alt, className, style, fallbackClassName, imageTestId, fallbackTestId }) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div className={fallbackClassName} data-testid={fallbackTestId}>
        Image unavailable
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      onError={() => setFailed(true)}
      data-testid={imageTestId}
    />
  );
};

const PollCard = ({ poll, onUpdate, onRemove, onReport }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [localPoll, setLocalPoll] = useState(poll);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [votersOpen, setVotersOpen] = useState(false);
  const [voters, setVoters] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editQuestion, setEditQuestion] = useState(poll.question || "");
  const [editOptions, setEditOptions] = useState(poll.options?.map((option) => option.text) || []);
  const [changingVote, setChangingVote] = useState(false);
  const ignoreMultiSubmitRef = useRef(false);
  const [timeTick, setTimeTick] = useState(Date.now());
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportStatus, setReportStatus] = useState(null);

  useEffect(() => {
    setLocalPoll(poll);
    setSelectedOptions(poll.allow_multiple && poll.user_vote ? poll.user_vote : []);
    setChangingVote(false);
  }, [poll]);


  const loadComments = useCallback(async () => {
    try {
      const response = await api.get(`/polls/${poll.id}/comments`);
      setComments(response.data);
    } catch (error) {
      toast.error("Unable to load comments.");
    }
  }, [poll.id]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  useEffect(() => {
    if (!localPoll.vote_change_expires_at) return;
    const interval = setInterval(() => setTimeTick(Date.now()), 30000);
    return () => clearInterval(interval);
  }, [localPoll.vote_change_expires_at]);

  const editable = useMemo(() => {
    if (!localPoll?.is_owner || !localPoll?.created_at) return false;
    const createdAt = new Date(localPoll.created_at).getTime();
    return Date.now() - createdAt <= 10 * 60 * 1000;
  }, [localPoll]);

  const hoursLeft = localPoll.timer_remaining_seconds
    ? Math.ceil(localPoll.timer_remaining_seconds / 3600)
    : 0;

  const questionMediaType = useMemo(() => {
    if (localPoll.question_media_type) return localPoll.question_media_type;
    if (localPoll.type && localPoll.type !== "text") return localPoll.type;
    return null;
  }, [localPoll.question_media_type, localPoll.type]);

  const questionMediaUrls = useMemo(() => {
    if (localPoll.question_media_urls && localPoll.question_media_urls.length > 0) {
      return localPoll.question_media_urls;
    }
    if (localPoll.media_urls && localPoll.media_urls.length > 0) {
      return localPoll.media_urls;
    }
    if (localPoll.media_url) return [localPoll.media_url];
    return [];
  }, [localPoll.media_url, localPoll.media_urls, localPoll.question_media_urls]);

  const voteChangeRemainingMinutes = useMemo(() => {
    if (!localPoll.vote_change_expires_at) return null;
    const diffMs = new Date(localPoll.vote_change_expires_at).getTime() - timeTick;
    return Math.max(0, Math.ceil(diffMs / 60000));
  }, [localPoll.vote_change_expires_at, timeTick]);

  const voteChangeAllowed = !!(voteChangeRemainingMinutes && voteChangeRemainingMinutes > 0);
  const showResults = localPoll.results_visible === true;
  const totalVotes = Number.isFinite(localPoll.total_votes)
    ? localPoll.total_votes
    : localPoll.vote_count || 0;
  const formattedTimestamp = useMemo(() => {
    if (!localPoll.created_at) return "Just now";
    const created = new Date(localPoll.created_at);
    if (Number.isNaN(created.getTime())) return "Just now";
    const now = timeTick;
    const diff = now - created.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return new Intl.DateTimeFormat("en-US", { day: "numeric", month: "long" }).format(created);
  }, [localPoll.created_at, timeTick]);

  const votersByOption = useMemo(() => {
    const grouped = {};
    localPoll.options.forEach((option) => {
      grouped[option.id] = [];
    });
    voters.forEach((voter) => {
      (voter.option_ids || []).forEach((optionId) => {
        if (grouped[optionId]) {
          grouped[optionId].push(voter);
        }
      });
    });
    return grouped;
  }, [localPoll.options, voters]);

  useEffect(() => {
    if (!voteChangeAllowed) {
      setChangingVote(false);
    }
  }, [voteChangeAllowed]);

  const handleVoteSubmit = useCallback(async (optionIds) => {
    if (!user) {
      navigate("/auth");
      return;
    }
    if (!optionIds.length) {
      toast.error("Select an option first.");
      return;
    }
    try {
      const response = await api.post(`/polls/${localPoll.id}/vote`, {
        option_ids: optionIds,
      });
      setLocalPoll(response.data);
      onUpdate?.(response.data);
      if (response.data.allow_multiple) {
        ignoreMultiSubmitRef.current = true;
        setSelectedOptions(response.data.user_vote || []);
      } else {
        setSelectedOptions([]);
      }
      setChangingVote(false);
      toast.success("Vote counted.");
    } catch (error) {
      toast.error("Unable to cast vote.");
    }
  }, [localPoll, navigate, onUpdate, user]);

  const toggleOption = (optionId) => {
    if (!localPoll.allow_multiple && localPoll.has_voted && !changingVote) return;
    if (localPoll.allow_multiple) {
      if (localPoll.has_voted && !voteChangeAllowed) return;
      if (localPoll.has_voted && !changingVote) {
        setChangingVote(true);
      }
      setSelectedOptions((prev) =>
        prev.includes(optionId)
          ? prev.filter((id) => id !== optionId)
          : [...prev, optionId]
      );
    } else {
      handleVoteSubmit([optionId]);
    }
  };

  useEffect(() => {
    if (!localPoll.allow_multiple) return;
    if (ignoreMultiSubmitRef.current) {
      ignoreMultiSubmitRef.current = false;
      return;
    }
    if (localPoll.has_voted && !changingVote) return;
    if (selectedOptions.length === 0) return;
    const timer = setTimeout(() => {
      handleVoteSubmit(selectedOptions);
    }, 600);
    return () => clearTimeout(timer);
  }, [selectedOptions, localPoll.allow_multiple, localPoll.has_voted, changingVote, handleVoteSubmit]);

  const handleStar = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    try {
      if (localPoll.starred) {
        await api.delete(`/polls/${localPoll.id}/star`);
        const updated = { ...localPoll, starred: false, star_count: localPoll.star_count - 1 };
        setLocalPoll(updated);
        onUpdate?.(updated);
      } else {
        await api.post(`/polls/${localPoll.id}/star`);
        const updated = { ...localPoll, starred: true, star_count: localPoll.star_count + 1 };
        setLocalPoll(updated);
        onUpdate?.(updated);
      }
    } catch (error) {
      toast.error("Unable to update star right now.");
    }
  };

  const handleSave = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    try {
      if (localPoll.saved) {
        await api.delete(`/polls/${localPoll.id}/save`);
        const updated = { ...localPoll, saved: false };
        setLocalPoll(updated);
        onUpdate?.(updated);
      } else {
        await api.post(`/polls/${localPoll.id}/save`);
        const updated = { ...localPoll, saved: true };
        setLocalPoll(updated);
        onUpdate?.(updated);
      }
    } catch (error) {
      toast.error("Unable to update save.");
    }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/poll/${localPoll.id}`);
      toast.success("Link copied.");
    } catch (error) {
      toast.error("Unable to copy link.");
    }
  };

  const handleReport = async () => {
    try {
      await api.post(`/polls/${localPoll.id}/report`);
      setReportStatus("success");
    } catch (error) {
      setReportStatus("error");
    }
    setReportModalOpen(true);
  };

  const handleComment = async (event) => {
    event.preventDefault();
    if (!user) {
      navigate("/auth");
      return;
    }
    if (!commentText.trim()) {
      toast.error("Write a short comment first.");
      return;
    }
    try {
      await api.post(`/polls/${localPoll.id}/comments`, { text: commentText.trim() });
      setCommentText("");
      await loadComments();
      const updated = { ...localPoll, comment_count: localPoll.comment_count + 1 };
      setLocalPoll(updated);
      onUpdate?.(updated);
    } catch (error) {
      toast.error("Unable to add comment.");
    }
  };

  const handleCommentDelete = async (commentId) => {
    try {
      await api.delete(`/polls/${localPoll.id}/comments/${commentId}`);
      setComments((prev) => prev.filter((comment) => comment.id !== commentId));
      const updated = {
        ...localPoll,
        comment_count: Math.max(0, (localPoll.comment_count || 1) - 1),
      };
      setLocalPoll(updated);
      onUpdate?.(updated);
      toast.success("Comment deleted.");
    } catch (error) {
      toast.error("Unable to delete comment.");
    }
  };

  const handleVotersOpen = async () => {
    setVotersOpen(true);
    try {
      const response = await api.get(`/polls/${localPoll.id}/voters`);
      setVoters(response.data);
    } catch (error) {
      toast.error("Vote list locked until you vote.");
    }
  };

  const startEdit = () => {
    setEditQuestion(localPoll.question || "");
    setEditOptions(localPoll.options.map((option) => option.text));
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      const response = await api.put(`/polls/${localPoll.id}`, {
        question: editQuestion,
        options: editOptions,
        allow_multiple: localPoll.allow_multiple,
        is_private: localPoll.is_private,
      });
      setLocalPoll(response.data);
      onUpdate?.(response.data);
      setEditOpen(false);
      toast.success("Poll updated. Votes reset.");
    } catch (error) {
      toast.error("Unable to update poll.");
    }
  };

  const deletePoll = async () => {
    try {
      await api.delete(`/polls/${localPoll.id}`);
      toast.success("Poll deleted.");
      onRemove?.(localPoll.id);
    } catch (error) {
      toast.error("Unable to delete poll.");
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 space-y-4 max-w-[600px] mx-auto" data-testid={`poll-card-${localPoll.id}`}>
      <div className="flex items-start justify-between gap-4">
        <button
          type="button"
          className="flex items-center gap-3 text-left"
          onClick={() => navigate(`/user/${localPoll.creator?.username}`)}
          data-testid={`poll-card-user-link-${localPoll.id}`}
        >
          <img
            src={localPoll.creator?.avatar_url}
            alt={localPoll.creator?.username}
            className="h-10 w-10 rounded-full object-cover cursor-pointer"
            onClick={() => navigate(`/user/${localPoll.creator?.username}`)}
            data-testid={`poll-card-avatar-${localPoll.id}`}
          />
          <div className="cursor-pointer" onClick={() => navigate(`/user/${localPoll.creator?.username}`)}>
            <div className="text-sm font-semibold" data-testid={`poll-card-username-${localPoll.id}`}>
              @{localPoll.creator?.username}
              {localPoll.creator?.verified && (
                <span className="ml-2 text-xs text-emerald-600" data-testid={`poll-card-verified-${localPoll.id}`}>
                  Verified
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground" data-testid={`poll-card-type-${localPoll.id}`}>
              {localPoll.type.toUpperCase()} poll
            </div>
          </div>
        </button>
        <div className="flex flex-col items-end gap-1">
          <div className="text-xs text-muted-foreground" data-testid={`poll-card-timestamp-${localPoll.id}`}>
            {formattedTimestamp}
          </div>
          {hoursLeft > 0 && (
            <div
              className="flex items-center gap-1 text-xs text-muted-foreground"
              data-testid={`poll-card-timer-${localPoll.id}`}
            >
              <Eye className="h-3 w-3" />
              Results unlock in {hoursLeft}h
            </div>
          )}
        </div>
      </div>

      {localPoll.question ? (
        <div className="text-lg font-semibold leading-snug" data-testid={`poll-card-question-${localPoll.id}`}>
          {localPoll.question}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground" data-testid={`poll-card-question-${localPoll.id}`}>
          Media-driven poll
        </div>
      )}

      {questionMediaType === "image" && questionMediaUrls.length > 0 && (
        <div className="rounded-xl border border-border bg-background/60 p-3" data-testid={`poll-card-image-context-${localPoll.id}`}>
          <div className="grid gap-2 sm:grid-cols-2" data-testid={`poll-card-image-context-grid-${localPoll.id}`}>
            {questionMediaUrls.map((url, index) => (
              <MediaImage
                key={index}
                src={withBackendUrl(url)}
                alt="Poll context"
                className="w-full rounded-lg object-cover"
                style={{ aspectRatio: "4 / 3" }}
                fallbackClassName="flex items-center justify-center rounded-lg bg-muted text-xs text-muted-foreground"
                imageTestId={`poll-card-image-context-media-${localPoll.id}-${index}`}
                fallbackTestId={`poll-card-image-context-fallback-${localPoll.id}-${index}`}
              />
            ))}
          </div>
        </div>
      )}

      {questionMediaType === "video" && questionMediaUrls[0] && (
        <div className="rounded-xl border border-border bg-background/60 p-3" data-testid={`poll-card-media-${localPoll.id}`}>
          <video
            src={withBackendUrl(questionMediaUrls[0])}
            controls
            className="w-full rounded-lg"
            data-testid={`poll-card-video-${localPoll.id}`}
          />
        </div>
      )}

      {questionMediaType === "audio" && questionMediaUrls[0] && (
        <div className="rounded-xl border border-border bg-background/60 p-3" data-testid={`poll-card-media-${localPoll.id}`}>
          <audio
            src={withBackendUrl(questionMediaUrls[0])}
            controls
            className="w-full"
            data-testid={`poll-card-audio-${localPoll.id}`}
          />
        </div>
      )}

      <div className="space-y-3" data-testid={`poll-card-options-${localPoll.id}`}>
        {localPoll.options.map((option) => {
          let optionVotes = Number.isFinite(option.vote_count)
            ? option.vote_count
            : Array.isArray(option.votes)
              ? option.votes.length
              : null;
          if (optionVotes === null) {
            console.log("[debug] missing option vote_count", { optionId: option.id, option });
            optionVotes = 0;
          }
          if (!Number.isFinite(localPoll.total_votes)) {
            console.log("[debug] missing poll total_votes", {
              pollId: localPoll.id,
              total_votes: localPoll.total_votes,
              fallback_vote_count: localPoll.vote_count,
            });
          }
          console.log("vote math", {
            total: totalVotes,
            optionVotes,
            optionId: option.id,
          });
          const optionPercentage = totalVotes > 0 ? (optionVotes / totalVotes) * 100 : 0;
        const isUserVote = localPoll.has_voted && (localPoll.user_vote || []).includes(option.id);
        return (
            <button
              key={option.id}
              type="button"
              className={`w-full rounded-xl border border-border p-4 text-left transition ${
              isUserVote
                ? "border-foreground bg-accent/40"
                : selectedOptions.includes(option.id)
                  ? "border-foreground"
                  : "hover:bg-accent"
            } ${localPoll.has_voted ? "cursor-default" : ""}`}
              onClick={() => toggleOption(option.id)}
              data-testid={`poll-option-${localPoll.id}-${option.id}`}
            >
              {option.media_url ? (
                <div className="space-y-2">
                  <MediaImage
                    src={withBackendUrl(option.media_url)}
                    alt={option.text}
                    className="w-full rounded-lg object-cover"
                    style={{ aspectRatio: "4 / 3" }}
                    fallbackClassName="flex h-40 items-center justify-center rounded-lg bg-muted text-xs text-muted-foreground"
                    imageTestId={`poll-option-image-${localPoll.id}-${option.id}`}
                    fallbackTestId={`poll-option-image-fallback-${localPoll.id}-${option.id}`}
                  />
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2" data-testid={`poll-option-label-${localPoll.id}-${option.id}`}>
                      <span>{option.text}</span>
                      {isUserVote && (
                        <span className="rounded-full bg-foreground/10 px-2 py-0.5 text-[10px] text-foreground" data-testid={`poll-option-your-vote-${localPoll.id}-${option.id}`}>
                          Your vote
                        </span>
                      )}
                    </div>
                    {showResults && (
                      <span data-testid={`poll-option-percent-${localPoll.id}-${option.id}`}>
                        {optionPercentage.toFixed(1)}%
                      </span>
                    )}
                  </div>
                  {showResults && (
                    <Progress value={optionPercentage} data-testid={`poll-option-progress-${localPoll.id}-${option.id}`} />
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2" data-testid={`poll-option-label-${localPoll.id}-${option.id}`}>
                      <span>{option.text}</span>
                      {isUserVote && (
                        <span className="rounded-full bg-foreground/10 px-2 py-0.5 text-[10px] text-foreground" data-testid={`poll-option-your-vote-${localPoll.id}-${option.id}`}>
                          Your vote
                        </span>
                      )}
                    </div>
                    {showResults && (
                      <span data-testid={`poll-option-percent-${localPoll.id}-${option.id}`}>
                        {optionPercentage.toFixed(1)}%
                      </span>
                    )}
                  </div>
                  {showResults && (
                    <Progress value={optionPercentage} data-testid={`poll-option-progress-${localPoll.id}-${option.id}`} />
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {!showResults && (
        <div className="text-sm text-muted-foreground" data-testid={`poll-card-hidden-${localPoll.id}`}>
          Vote to unlock full results.
        </div>
      )}


      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground" data-testid={`poll-card-counts-${localPoll.id}`}>
        <button
          onClick={handleVotersOpen}
          className="text-left"
          data-testid={`poll-card-vote-count-${localPoll.id}`}
        >
          {localPoll.vote_count} votes
        </button>
        <span data-testid={`poll-card-comment-count-${localPoll.id}`}>{localPoll.comment_count} comments</span>
        <span data-testid={`poll-card-star-count-${localPoll.id}`}>{localPoll.star_count} stars</span>
        {localPoll.has_voted && voteChangeAllowed && !changingVote && (
          <button
            className="text-xs text-muted-foreground underline"
            onClick={() => {
              setChangingVote(true);
              setSelectedOptions(localPoll.user_vote || []);
            }}
            data-testid={`poll-card-change-vote-${localPoll.id}`}
          >
            Change your opinion (locks in {voteChangeRemainingMinutes}m)
          </button>
        )}
        {localPoll.has_voted && !voteChangeAllowed && (
          <span className="text-xs text-muted-foreground" data-testid={`poll-card-vote-locked-${localPoll.id}`}>
            Vote locked
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid={`poll-card-actions-${localPoll.id}`}>
        <button
          className="h-9 w-9 rounded-full border border-border flex items-center justify-center"
          onClick={handleStar}
          data-testid={`poll-card-star-button-${localPoll.id}`}
        >
          <Star
            className={`h-4 w-4 ${localPoll.starred ? "fill-[#FFC235] text-[#FFC235]" : "text-muted-foreground"}`}
          />
        </button>
        <button
          className="h-9 w-9 rounded-full border border-border flex items-center justify-center"
          onClick={handleSave}
          data-testid={`poll-card-save-button-${localPoll.id}`}
        >
          <Bookmark
            className={`h-4 w-4 ${localPoll.saved ? "fill-[#FFC235] text-[#FFC235]" : "text-muted-foreground"}`}
          />
        </button>
        <button
          className="h-9 w-9 rounded-full border border-border flex items-center justify-center"
          onClick={handleShare}
          data-testid={`poll-card-share-button-${localPoll.id}`}
        >
          <Share2 className="h-4 w-4" />
        </button>
        <button
          className="h-9 w-9 rounded-full border border-border flex items-center justify-center"
          onClick={handleReport}
          data-testid={`poll-card-report-button-${localPoll.id}`}
        >
          <Flag className="h-4 w-4" />
        </button>
        {localPoll.is_owner && editable && (
          <button
            className="h-9 w-9 rounded-full border border-border flex items-center justify-center"
            onClick={startEdit}
            data-testid={`poll-card-edit-button-${localPoll.id}`}
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
        {localPoll.is_owner && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                className="h-9 w-9 rounded-full border border-border flex items-center justify-center"
                data-testid={`poll-card-delete-button-${localPoll.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid={`poll-card-delete-dialog-${localPoll.id}`}>
              <AlertDialogHeader>
                <AlertDialogTitle data-testid={`poll-card-delete-title-${localPoll.id}`}>
                  Delete this poll?
                </AlertDialogTitle>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid={`poll-card-delete-cancel-${localPoll.id}`}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction onClick={deletePoll} data-testid={`poll-card-delete-confirm-${localPoll.id}`}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <div className="space-y-4" data-testid={`poll-card-comments-${localPoll.id}`}>
        <form className="space-y-2" onSubmit={handleComment} data-testid={`poll-card-comment-form-${localPoll.id}`}>
          <Textarea
            rows={2}
            value={commentText}
            onChange={(event) => setCommentText(event.target.value)}
            placeholder="Add a short comment"
            data-testid={`poll-card-comment-input-${localPoll.id}`}
          />
          <Button type="submit" variant="secondary" data-testid={`poll-card-comment-submit-${localPoll.id}`}>
            Comment
          </Button>
        </form>
        <div className="space-y-3" data-testid={`poll-card-comment-list-${localPoll.id}`}>
          {comments.length === 0 ? (
            <div className="text-sm text-muted-foreground" data-testid={`poll-card-comment-empty-${localPoll.id}`}>
              No comments yet.
            </div>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="flex items-start gap-3" data-testid={`poll-card-comment-${localPoll.id}-${comment.id}`}>
                <button
                  type="button"
                  className="flex items-start gap-3 text-left"
                  onClick={() => navigate(`/user/${comment.username}`)}
                  data-testid={`poll-card-comment-user-link-${localPoll.id}-${comment.id}`}
                >
                  <img
                    src={comment.avatar_url}
                    alt={comment.username}
                    className="h-8 w-8 rounded-full object-cover"
                    data-testid={`poll-card-comment-avatar-${localPoll.id}-${comment.id}`}
                  />
                  <div>
                    <div className="text-sm font-semibold" data-testid={`poll-card-comment-user-${localPoll.id}-${comment.id}`}>
                      @{comment.username}
                    </div>
                    <div className="text-sm text-muted-foreground" data-testid={`poll-card-comment-text-${localPoll.id}-${comment.id}`}>
                      {comment.text}
                    </div>
                  </div>
                </button>
                <div className="flex-1" />
                {(user?.id === comment.user_id || user?.id === localPoll.creator_id) && (
                  <button
                    className="h-8 w-8 rounded-full border border-border flex items-center justify-center"
                    onClick={() => handleCommentDelete(comment.id)}
                    data-testid={`poll-card-comment-delete-${localPoll.id}-${comment.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <Dialog open={votersOpen} onOpenChange={setVotersOpen}>
        <DialogContent data-testid={`poll-card-voters-dialog-${localPoll.id}`}>
          <DialogHeader>
            <DialogTitle data-testid={`poll-card-voters-title-${localPoll.id}`}>Voters</DialogTitle>
          </DialogHeader>
          <div className="space-y-6" data-testid={`poll-card-voters-list-${localPoll.id}`}>
            {voters.length === 0 ? (
              <div className="text-sm text-muted-foreground" data-testid={`poll-card-voters-empty-${localPoll.id}`}>
                No voters to display.
              </div>
            ) : (
              localPoll.options.map((option) => {
                const optionVotes = option.vote_count || 0;
                const optionPercentage = totalVotes > 0 ? (optionVotes / totalVotes) * 100 : 0;
                const optionVoters = votersByOption[option.id] || [];
                return (
                  <div key={option.id} className="space-y-2" data-testid={`poll-card-voters-group-${localPoll.id}-${option.id}`}>
                    <div className="flex items-center justify-between text-sm font-semibold">
                      <span data-testid={`poll-card-voters-option-label-${localPoll.id}-${option.id}`}>
                        {option.text}
                      </span>
                      <span data-testid={`poll-card-voters-option-percent-${localPoll.id}-${option.id}`}>
                        {optionPercentage.toFixed(1)}%
                      </span>
                    </div>
                    <div className="border-t border-border" data-testid={`poll-card-voters-divider-${localPoll.id}-${option.id}`} />
                    {optionVoters.length === 0 ? (
                      <div className="text-xs text-muted-foreground" data-testid={`poll-card-voters-none-${localPoll.id}-${option.id}`}>
                        No votes yet.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {optionVoters.map((voter) => (
                          <button
                            key={`${option.id}-${voter.id}`}
                            type="button"
                            className="flex items-center gap-3 text-left"
                            onClick={() => navigate(`/user/${voter.username}`)}
                            data-testid={`poll-card-voter-${localPoll.id}-${option.id}-${voter.id}`}
                          >
                            <img
                              src={voter.avatar_url}
                              alt={voter.username}
                              className="h-8 w-8 rounded-full object-cover"
                              data-testid={`poll-card-voter-avatar-${localPoll.id}-${option.id}-${voter.id}`}
                            />
                            <div>
                              <div className="text-sm font-semibold" data-testid={`poll-card-voter-username-${localPoll.id}-${option.id}-${voter.id}`}>
                                @{voter.username}
                              </div>
                              <div className="text-xs text-muted-foreground" data-testid={`poll-card-voter-name-${localPoll.id}-${option.id}-${voter.id}`}>
                                {voter.full_name}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid={`poll-card-edit-dialog-${localPoll.id}`}>
          <DialogHeader>
            <DialogTitle data-testid={`poll-card-edit-title-${localPoll.id}`}>Edit poll</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium" data-testid={`poll-card-edit-question-label-${localPoll.id}`}>
                Question
              </label>
              <Textarea
                rows={3}
                value={editQuestion}
                onChange={(event) => setEditQuestion(event.target.value)}
                data-testid={`poll-card-edit-question-input-${localPoll.id}`}
              />
            </div>
            <div className="space-y-2" data-testid={`poll-card-edit-options-${localPoll.id}`}>
              <label className="text-sm font-medium" data-testid={`poll-card-edit-options-label-${localPoll.id}`}>
                Options
              </label>
              {editOptions.map((option, index) => (
                <Input
                  key={index}
                  value={option}
                  onChange={(event) =>
                    setEditOptions((prev) => prev.map((item, idx) => (idx === index ? event.target.value : item)))
                  }
                  data-testid={`poll-card-edit-option-${localPoll.id}-${index}`}
                />
              ))}
            </div>
            <Button onClick={saveEdit} data-testid={`poll-card-edit-save-${localPoll.id}`}>
              Save changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={reportModalOpen}
        onOpenChange={(open) => {
          setReportModalOpen(open);
          if (!open && reportStatus === "success") {
            onReport?.(localPoll.id);
          }
        }}
      >
        <DialogContent className="max-w-sm" data-testid={`poll-card-report-modal-${localPoll.id}`}>
          <DialogHeader>
            <DialogTitle data-testid={`poll-card-report-title-${localPoll.id}`}>
              {reportStatus === "success" ? "Report submitted" : "Report failed"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground" data-testid={`poll-card-report-body-${localPoll.id}`}>
            {reportStatus === "success"
              ? "Thank you. We will review this poll."
              : "Please try again."}
          </div>
          <Button onClick={() => setReportModalOpen(false)} data-testid={`poll-card-report-close-${localPoll.id}`}>
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PollCard;
